import Stack from '@mui/material/Stack'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Tooltip from '@mui/material/Tooltip'

import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'

import IconButton from '@mui/material/IconButton'
import Edit from '@mui/icons-material/Edit'
import Check from '@mui/icons-material/Check'

import { useEffect, useRef, useState } from 'react'

import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'

import durationToStr from '@/libs/utils/durationToStr'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { alpha } from '@mui/material/styles'
import ReactPlayer from 'react-player'
import FormItem from '@/shared/FormCreator/FormItem'
import { useForm } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'

const mapStateToProps = ({ loading }) => ({
  isLoading: loading['courseTemplate/getCourseTemplateById'] || loading['user/editUserGroup'],
})

const PreviewCourseVideoDialog = ({
  dispatch,
  isLoading,
  onClose,
  onComplete,
  courseData,
  editable = true,
  ...props
}) => {
  const { t } = useTranslation()
  const form = useRef()

  const [page, setPage] = useState(0)
  const [courseDetails, setCourseDetails] = useState()
  const [categories, setCategories] = useState([])
  const [courseName, setCourseName] = useState(courseData?.name || '')
  const [isEditName, setIsEditName] = useState(false)

  const { control, watch } = useForm({ defaultValues: { name: courseName } })

  const nameInput = watch('name')

  const handleClose = () => {
    onClose?.()
  }

  const handleSubmit = () => {
    const { handleSubmit: formSubmit } = form.current

    formSubmit?.(values => {
      dispatch({
        type: 'courseTemplate/editCourseTemplate',
        payload: {
          desc: values.courseTempDesc,
          name: courseName,
          courseTempId: courseData.templateId,
        },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.course_updated', { name: courseName }),
              severity: 'success',
            },
          })

          onComplete?.()
          handleClose?.()
        },
        errorCallback: message => {
          form.current.setError('email', { type: 'custom', message })
        },
      })
    })?.()
  }

  useEffect(() => {
    if (courseData) {
      dispatch({
        type: 'courseTemplate/getCourseTemplateById',
        payload: { courseTempId: courseData.templateId },
        successCallback: responseData => {
          form.current?.setValue?.('courseTempDesc', responseData.courseDesc)
          setCourseDetails(responseData)
          const { courseTempLabelList } = responseData
          const transformedOptions = courseTempLabelList.map(({ name }) => name)

          if (transformedOptions) {
            const number = transformedOptions.length
            const divisor = 5
            const maxLength = number - (number % divisor) + divisor * !!(number % divisor)
            const emptyArr = [...Array(maxLength - transformedOptions.length)]

            emptyArr.forEach(() => {
              transformedOptions.push('')
            })

            setCategories(transformedOptions)
          }
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData])

  return (
    <ConfirmDialog
      {...props}
      open={!!courseData}
      hideCancel
      actionsSx={{ alignSelf: 'flex-end' }}
      title={
        <Stack
          sx={{
            position: 'absolute',
            flexDirection: 'row',
            top: 12,
            left: 0,
            right: 0,
            p: 1,
            pl: 4,
            columnGap: 1,
            alignItems: 'center',
            borderBottom: '1px solid black',
          }}
        >
          {isEditName ? (
            <FormItem control={control} name="name" />
          ) : (
            <Typography color={theme => theme.palette.secondary_80.main} fontWeight="bold">
              {nameInput}
            </Typography>
          )}
          <IconButton
            color={isEditName ? 'primary' : 'secondary_80'}
            disabled={isEditName && isEmpty(nameInput)}
            onClick={() => {
              if (isEditName) {
                setCourseName(nameInput)
              }
              setIsEditName(!isEditName)
            }}
          >
            {isEditName ? <Check /> : <Edit />}
          </IconButton>
        </Stack>
      }
      onClose={handleClose}
      onSubmit={() => {
        if (editable) {
          handleSubmit()
        } else {
          handleClose?.()
        }
      }}
      loading={isLoading}
      contentProps={{ sx: { width: 500 } }}
    >
      <Stack my={1} direction="row" justifyContent="flex-start">
        <ReactPlayer height={'auto'} controls={true} url={courseDetails?.courseSrc} />
      </Stack>

      <Stack
        direction="row"
        flexWrap="nowrap"
        gap={1}
        alignItems="center"
        justifyContent="flex-end"
      >
        <Tooltip title={courseDetails?.name} placement="top-end">
          <Typography
            noWrap
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
            }}
          >
            {courseDetails?.courseName}
          </Typography>
        </Tooltip>
        <Typography display="flex" justifyContent="center" variant="body2">
          <ThumbUpAltIcon sx={{ color: '#F60' }} />
          {courseDetails?.goodRatingCnt ?? '-'}
        </Typography>
        <Stack flex={1} />
        <Typography variant="caption" color="#00000040" sx={{ textAlign: 'right' }}>
          {durationToStr(courseDetails?.courseLength)}
        </Typography>
      </Stack>

      <Card elevation={0} sx={{ my: 2 }}>
        <CardContent
          sx={{
            paddingBottom: '0px !important',
            paddingTop: 0,
            backgroundColor: theme => alpha(theme.palette.secondary_80.main, 0.1),
            px: 0,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="flex-start">
            <Stack
              flexDirection={'row'}
              flex={1}
              alignItems={'center'}
              justifyContent={'space-evenly'}
              sx={{ overflow: 'hidden', height: 30 }}
            >
              {categories.slice(Math.max(0, page * 5), page * 5 + 5).map((category, index) => (
                <Tooltip key={`category_${index + 1}`} title={category}>
                  <Typography color={theme => theme.palette.secondary_80.main}>
                    {category}
                  </Typography>
                </Tooltip>
              ))}
            </Stack>

            <Stack flexDirection={'row'}>
              <IconButton
                disabled={page === 0}
                onClick={() => {
                  setPage?.(page - 1)
                }}
                color="secondary_80"
              >
                <ArrowDropUp />
              </IconButton>
              <IconButton
                disabled={page * 5 + 5 >= categories.length}
                onClick={() => {
                  setPage?.(page + 1)
                }}
                color="secondary_80"
              >
                <ArrowDropDown />
              </IconButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <FormCreator
        ref={form}
        gridItemProps={{ md: 3 }}
        formItems={[
          {
            label: t('general.course_intro'),
            name: 'courseTempDesc',
            defaultValue: courseDetails?.courseDesc,
            inputConfig: {
              disabled: !editable,
              inputBaseProps: {
                labelSuffix: '',
                flexDirection: 'column',
                alignItems: 'stretch',
                spacing: 0,
                labelProps: { sx: { color: theme => theme.palette.btn_positive.main } },
              },
              variant: 'outlined',
              rows: 5,
              multiline: true,
            },
          },
        ]}
        submitComponent={() => null}
        resetComponent={() => null}
      />
      <Typography variant="caption" color={theme => theme.palette.grey_60.main}>
        {t('course.notice')}
      </Typography>
    </ConfirmDialog>
  )
}

export default connect(mapStateToProps)(PreviewCourseVideoDialog)
